import * as React from 'react'

import { Check } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '~/client/src/shared/components/Button/Button'
import {
  Tooltip,
  TooltipContent,
} from '~/client/src/shared/components/shadcn-ui/tooltip'

const TILE_CLASS = 'tw:relative tw:size-10 tw:border-0'
// Full-bleed art hides the button's own background, so hover tints over it.
const FULL_BLEED_CLASS =
  'tw:p-0 tw:hover:bg-transparent tw:after:pointer-events-none tw:after:absolute tw:after:inset-0 tw:after:rounded-md tw:hover:after:bg-foreground/4'
const GLYPH_CLASS = 'tw:p-2 tw:hover:bg-accent'

let lastTypeNameId = 0

interface ITileTooltip {
  show: (tile: HTMLElement, content: React.ReactNode) => void
  hide: () => void
}

const TileTooltipContext = React.createContext<ITileTooltip>({
  show: () => undefined,
  hide: () => undefined,
})

/**
 * One tooltip for every tile inside, pointed at the hovered or focused one: a tooltip trigger per
 * tile was two thirds of the cost of opening a 200-tile list.
 */
export function IconPickerTileTooltip({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement {
  const [isOpen, setIsOpen] = React.useState(false)
  // Kept while closing, so the popup leaves from where it was.
  const [tip, setTip] = React.useState<{
    anchor: HTMLElement
    content: React.ReactNode
  } | null>(null)
  const tooltip = React.useMemo<ITileTooltip>(
    () => ({
      show: (anchor, content) => {
        setTip({ anchor, content })
        setIsOpen(true)
      },
      hide: () => setIsOpen(false),
    }),
    [],
  )

  return (
    <TileTooltipContext.Provider value={tooltip}>
      {children}
      <Tooltip open={isOpen} onOpenChange={setIsOpen}>
        <TooltipContent side="bottom" anchor={tip?.anchor}>
          {tip?.content}
        </TooltipContent>
      </Tooltip>
    </TileTooltipContext.Provider>
  )
}

interface IProps {
  label: string
  /** The type the icon is listed for, shown beside its name in the tooltip. */
  typeName?: string
  isSelected: boolean
  isFullBleed?: boolean
  onClick: () => void
  children: React.ReactNode
}

export default function IconPickerTile({
  label,
  typeName,
  isSelected,
  isFullBleed,
  onClick,
  children,
}: IProps): React.ReactElement {
  const { show, hide } = React.useContext(TileTooltipContext)
  const [typeNameId] = React.useState(
    () => `icon-picker-tile-type-${++lastTypeNameId}`,
  )
  const content = typeName ? (
    <span className="tw:flex tw:flex-wrap tw:items-baseline tw:gap-x-1.5">
      {label}
      <span className="tw:font-mono tw:whitespace-nowrap tw:text-background/70">
        {typeName}
      </span>
    </span>
  ) : (
    label
  )

  return (
    <Button
      type="button"
      variant="ghost"
      aria-label={label}
      aria-describedby={typeName ? typeNameId : undefined}
      aria-pressed={isSelected}
      onClick={() => {
        hide()
        onClick()
      }}
      onPointerEnter={event => {
        if (event.pointerType !== 'touch') show(event.currentTarget, content)
      }}
      onPointerLeave={hide}
      onFocus={event => {
        if (event.currentTarget.matches(':focus-visible')) {
          show(event.currentTarget, content)
        }
      }}
      onBlur={hide}
      className={cn(
        TILE_CLASS,
        isFullBleed ? FULL_BLEED_CLASS : GLYPH_CLASS,
        isSelected && 'tw:ring-2 tw:ring-ring',
      )}
    >
      {children}
      {typeName && (
        <span id={typeNameId} className="tw:sr-only">
          {typeName}
        </span>
      )}
      {isSelected && (
        <span
          data-slot="icon-picker-check"
          className="tw:absolute tw:-top-1.5 tw:-right-1.5 tw:flex tw:size-[18px] tw:items-center tw:justify-center tw:rounded-full tw:bg-primary tw:text-primary-foreground"
        >
          <Check className="tw:size-3" />
        </span>
      )}
    </Button>
  )
}
